import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Space, Typography, Alert, Divider, message } from 'antd';
import {
  lookupMainMeterBySubMeter,
  mqttPublishByMeter,
  getMqttMappingByMeter,
  getMqttLatestMessage,
} from '../api';
import {
  getAdminMeterPublishKey,
  remotePayloadUseBleDirect,
  buildFinalPayload,
  buildRemoteRebootHex,
  getRxAddressCandidates,
  rxTopicsForHttpPoll,
  yjRemoteMqttRxWaitMsByMappingTier,
  buildMqttMessagePollOpts,
} from '../utils/yjMeterRemote';
import { createMqttWS } from '../utils/mqttWS';

const { Text, Paragraph } = Typography;

const isMainMeter = (device) => Number(device?.device_detail_type) === 1;

const meterNoOf = (device) =>
  String(device?.current_meter_no || device?.factory_meter_no || '').trim();

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/** 仅本弹窗用：解析 52 重启回包 ACK（与 test4 一致：00=成功，01=失败） */
function parseReboot52Ack(rawMsg, addressCandidates) {
  const msg = String(rawMsg || '').trim().toLowerCase().replace(/\s+/g, '');
  if (msg.length < 18 || msg.substring(12, 14) !== '52') return null;
  const callbackMeterNo = msg.substring(0, 12);
  const candidates = addressCandidates || [];
  if (candidates.length > 0 && !candidates.includes(callbackMeterNo)) return null;
  const ackHex = msg.substring(16, 18);
  const ackDec = parseInt(ackHex, 16);
  if (!Number.isFinite(ackDec)) return null;
  return { ok: ackDec === 0, ackDec, ackHex };
}

/**
 * 重启电表 + 查主表表号
 * 重启反馈对齐 test4 轻量版：弹窗内 Alert + 等 52 回包 / 超时（不改其它页面）
 */
const MeterRebootModal = ({ open, device, onClose }) => {
  const [rebootLoading, setRebootLoading] = useState(false);
  const [rebootHint, setRebootHint] = useState('');
  const [rebootError, setRebootError] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState('');
  const [mainMeterNos, setMainMeterNos] = useState([]);
  const cancelledRef = useRef(false);

  useEffect(() => {
    if (!open) {
      cancelledRef.current = true;
      return;
    }
    cancelledRef.current = false;
    setRebootLoading(false);
    setRebootHint('');
    setRebootError('');
    setLookupLoading(false);
    setLookupError('');
    if (isMainMeter(device)) {
      const no = meterNoOf(device);
      setMainMeterNos(no ? [no] : []);
    } else {
      setMainMeterNos([]);
    }
  }, [open, device?.id]);

  const waitRebootAck = async (deviceRow, publishKey, publishAt) => {
    const mapRes = await getMqttMappingByMeter(publishKey);
    if (!mapRes?.success || !mapRes?.data?.rx_topic) {
      return { kind: 'no_mapping', msg: mapRes?.msg || '未找到 MQTT 主题映射，无法等待表端回执' };
    }
    const mapData = mapRes.data;
    const tier = Number(mapData.mapping_fanout_tier) === 2 ? 2 : 1;
    const waitMs = yjRemoteMqttRxWaitMsByMappingTier(tier);
    const rxTopics = rxTopicsForHttpPoll(mapData);
    const candidates = getRxAddressCandidates(deviceRow);
    const pollOpts = buildMqttMessagePollOpts(publishKey, 'reboot', publishAt);

    let fromWs = null;
    let wsHandle = null;
    if (mapData.rx_topic) {
      const extraRx = tier === 2 ? rxTopics.filter((t) => t !== mapData.rx_topic) : [];
      wsHandle = createMqttWS(
        mapData.rx_topic,
        (_topic, msg, hex) => {
          const p = parseReboot52Ack(String(hex || msg || ''), candidates);
          if (p) fromWs = p;
        },
        { extraRxTopics: extraRx }
      );
      try {
        await Promise.race([wsHandle.ready, sleep(3000)]);
      } catch (_) {
        wsHandle = null;
      }
    }

    const deadline = Date.now() + waitMs;
    let lastRaw = '';
    try {
      while (Date.now() < deadline) {
        if (cancelledRef.current) return { kind: 'cancelled' };
        if (fromWs) {
          return { kind: 'ack', ...fromWs };
        }
        for (let i = 0; i < rxTopics.length; i++) {
          try {
            const msgRes = await getMqttLatestMessage(rxTopics[i], 'raw', pollOpts);
            const raw = String(msgRes?.data?.message ?? msgRes?.data?.hex ?? '').trim();
            if (!raw || raw === lastRaw) continue;
            lastRaw = raw;
            const p = parseReboot52Ack(raw, candidates);
            if (p) return { kind: 'ack', ...p };
          } catch (_) {
            /* 单主题轮询失败忽略，继续其它主题 */
          }
        }
        await sleep(400);
      }
    } finally {
      try {
        wsHandle?.close();
      } catch (_) {}
    }
    return { kind: 'timeout', waitMs };
  };

  const handleReboot = async () => {
    if (!device) return;
    setRebootLoading(true);
    setRebootHint('正在下发重启指令…');
    setRebootError('');
    try {
      const publishKey = getAdminMeterPublishKey(device);
      if (!publishKey) throw new Error('无法确定表号或 MAC，请检查设备档案');
      const isBleDirect = remotePayloadUseBleDirect(publishKey);
      const payload = buildFinalPayload(buildRemoteRebootHex(publishKey, isBleDirect), true);
      const publishAt = Date.now();
      const pubRes = await mqttPublishByMeter({ meter_no: publishKey, payload, qos: 1 });
      if (!pubRes?.success) {
        throw new Error(pubRes?.msg || pubRes?.error || '指令发送失败');
      }
      if (cancelledRef.current) return;
      setRebootHint('重启指令已下发，等待表端回执…');

      const wait = await waitRebootAck(device, publishKey, publishAt);
      if (cancelledRef.current || wait.kind === 'cancelled') return;

      if (wait.kind === 'ack') {
        if (wait.ok) {
          setRebootHint('重启成功（表端已回执）');
          setRebootError('');
          message.success('重启成功');
        } else {
          setRebootHint('');
          setRebootError(`重启失败（表端回执 ACK=${wait.ackHex || wait.ackDec}）`);
          message.error('重启失败');
        }
        return;
      }
      if (wait.kind === 'no_mapping') {
        // 云端已发出，但无法等回执：明确告知，避免误以为失败
        setRebootHint('重启指令已下发（未找到主题映射，无法等待表端回执）');
        setRebootError('');
        message.warning('指令已下发，但无法等待回执');
        return;
      }
      setRebootHint('');
      setRebootError('重启失败（未收到表端回执）');
      message.error('重启失败（未收到表端回执）');
    } catch (e) {
      const errMsg = e?.response?.data?.msg || e?.message || '重启失败';
      setRebootHint('');
      setRebootError(errMsg);
      message.error(errMsg);
    } finally {
      if (!cancelledRef.current) setRebootLoading(false);
    }
  };

  const handleLookupMain = async () => {
    if (!device) return;
    const q = meterNoOf(device);
    if (!q) {
      message.warning('当前设备无表号，无法查询主表');
      return;
    }
    setLookupLoading(true);
    setLookupError('');
    try {
      const res = await lookupMainMeterBySubMeter(q);
      if (!res?.success) {
        setLookupError(res?.msg || '查询失败');
        return;
      }
      const list = Array.isArray(res.data?.main_meters) ? res.data.main_meters : [];
      const nos = [];
      list.forEach((m) => {
        const no = String(m?.current_meter_no || m?.factory_meter_no || '').trim();
        if (no && !nos.includes(no)) nos.push(no);
      });
      setMainMeterNos(nos);
      if (!res.data?.latest_rx_topic) {
        setLookupError('未找到该表最近心跳，无法反查主表');
      } else if (!nos.length) {
        setLookupError('已找到 RX 主题，但未匹配到主表');
      } else {
        message.success(`找到 ${nos.length} 个主表表号`);
      }
    } catch (e) {
      setLookupError(e?.response?.data?.msg || e?.message || '查询失败');
    } finally {
      setLookupLoading(false);
    }
  };

  const meterLabel = meterNoOf(device) || device?.device_mac || device?.id || '';
  const selfIsMain = isMainMeter(device);

  return (
    <Modal
      title={`重启 / 主表${meterLabel ? ` · ${meterLabel}` : ''}`}
      open={open}
      onCancel={onClose}
      footer={null}
      width={460}
      destroyOnClose
      maskClosable={false}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <div>
          <Text strong>重启电表</Text>
          <Paragraph type="secondary" style={{ marginBottom: 8, marginTop: 4 }}>
            先下发重启指令，再等待表端 52 回执（与小程序 test4 一致）；超时会提示未收到回执。
          </Paragraph>
          <Button danger type="primary" loading={rebootLoading} onClick={handleReboot}>
            重启电表
          </Button>
          <div style={{ minHeight: 40, marginTop: 8 }}>
            {rebootError ? (
              <Alert type="error" showIcon message={rebootError} />
            ) : rebootHint ? (
              <Alert type={rebootLoading ? 'info' : 'success'} showIcon message={rebootHint} />
            ) : null}
          </div>
        </div>

        <Divider style={{ margin: '4px 0' }} />

        <div>
          <Text strong>主表表号</Text>
          <Paragraph type="secondary" style={{ marginBottom: 8, marginTop: 4 }}>
            {selfIsMain
              ? '当前设备已是 4G 主表，直接显示本表表号。'
              : '当前为副表：可按「主表查询」同款逻辑反查主表表号。'}
          </Paragraph>

          {selfIsMain ? (
            <Alert
              type="success"
              showIcon
              message={
                mainMeterNos.length
                  ? `主表表号：${mainMeterNos.join('、')}`
                  : '主表档案缺少表号'
              }
            />
          ) : (
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <Button loading={lookupLoading} onClick={handleLookupMain}>
                查询主表
              </Button>
              {mainMeterNos.length > 0 ? (
                <Alert
                  type="success"
                  showIcon
                  message={`主表表号：${mainMeterNos.join('、')}`}
                />
              ) : null}
              {lookupError ? <Alert type="error" showIcon message={lookupError} /> : null}
            </Space>
          )}
        </div>
      </Space>
    </Modal>
  );
};

export default MeterRebootModal;
